import { useEffect, useMemo, useRef, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, usePermissions, useTranslate } from "@refinedev/core";
import {
  Banknote,
  Calendar,
  Check,
  CreditCard,
  Download,
  ExternalLink,
  // Aliased: a bare `History` binding shadows nothing but reads as the DOM global, and a stale Vite
  // dep-optimize cache resolved it to `window.History` — React then tried to construct it.
  HistoryIcon,
  Home,
  Landmark,
  Mail,
  MoreHorizontal,
  PawPrint,
  Pencil,
  Phone,
  Plus,
  Repeat,
  Send,
  Smartphone,
  Trash2,
  Truck,
  ArrowLeft,
} from "lucide-react";
import { PlusCircleSolidIcon,
  Button,
  CheckboxField,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  type StatusTone,
  TextareaField,
} from "@fawkes/ui";

import { formatDate, formatDateTime, formatEuro, todayIso } from "../i18n/format";
import { downloadBetalingenCsv, downloadFactuurPdf } from "../api/factuurExport";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { ProductTariefPicker, type GekozenProductRegel } from "../components/ProductTariefPicker";
import "./FactuurDetail.css";
import { IndeelbareTabel } from "../components/IndeelbareTabel";
import { volledigeNaam } from "./RelatieDetail";

// ---- types (mirror api FactuurDetailResponse) ------------------------------------------------------
type FactuurStatus = "CONCEPT" | "BETAALD" | "OPENSTAAND" | "VERVALLEN";

interface KlantGegevens {
  id: number | null;
  naam: string | null;
  straat: string | null;
  huisnummer: string | null;
  huisnummerToevoeging: string | null;
  postcode: string | null;
  woonplaats: string | null;
  land: string | null;
  telefoon: string | null;
  email: string | null;
  debiteurnummer: string | null;
}
interface Samenvatting {
  subtotaalExclBtw: number;
  btwBedrag: number;
  kortingBedrag: number;
  totaal: number;
  betaald: number;
  openstaand: number;
}
interface BtwRegel {
  percentage: number;
  grondslag: number;
  bedrag: number;
}
interface RegelRij {
  id: number;
  categorie: string;
  omschrijving: string;
  subOmschrijving: string | null;
  aantal: number;
  prijs: number;
  btwPercentage: number;
  totaal: number;
}
interface BetalingRij {
  id: number;
  datum: string | null;
  methode: string | null;
  referentie: string | null;
  bedrag: number;
  geannuleerd: boolean;
}
interface TijdlijnEvent {
  moment: string;
  titel: string;
  detail: string | null;
  type: string;
  /** Wie het deed; alleen bij acties die in de applicatie zijn vastgelegd. */
  door?: string | null;
  /** Van het moment is alleen de dag bekend: dan geen "00:00" erachter. */
  alleenDatum?: boolean;
}
// One dated entry of the per-type invoice notes-log (Epic 6 part 2; mirrors api FactuurNotitieResponse).
// Distinct from the single `opmerking` memo — this is an append-only dated log, shown on the design's
// Opmerkingen section as the internal notes ("niet zichtbaar voor klant").
interface NotitieRij {
  id: number;
  datum: string | null;
  melder: string | null;
  notitie: string;
  opmerking: string | null;
  /** "oud": uit de tabel van het factuurtype; "nieuw": uit de losse notitietabel. */
  bron?: "oud" | "nieuw";
}
// What this invoice is about: the animal, its shelter stay, and the ambulance that brought it in
// (api FactuurDetailResponse.Koppeling). Only the fields belonging to `type` are filled.
interface Koppeling {
  type: "DIER" | "VERBLIJF" | "AMBULANCE";
  dierId: number | null;
  naam: string | null;
  chipnummer: string | null;
  diersoort: string | null;
  geslacht: string | null;
  geboortedatum: string | null;
  verblijfId: number | null;
  verblijfNummer: number | null;
  opnameDatum: string | null;
  vertrekDatum: string | null;
  ritnummer: string | null;
}
interface FactuurDetailData {
  id: number;
  /** Leeg zolang de factuur nog geen nummer heeft; die komt bij de eerste wijziging. */
  nummer: string | null;
  factuurType: string | null;
  factuurDatum: string | null;
  vervalDatum: string | null;
  status: FactuurStatus;
  externeReferentie: string | null;
  opmerking: string | null;
  betaalmethode: string | null;
  klant: KlantGegevens | null;
  samenvatting: Samenvatting;
  btwUitsplitsing: BtwRegel[];
  regels: RegelRij[];
  betalingen: BetalingRij[];
  tijdlijn: TijdlijnEvent[];
  koppelingen: Koppeling[];
}

// ---- de conceptlaag ---------------------------------------------------------------------------
//
// Een factuur schreef elke wijziging meteen weg: een regel bijwerken was een PUT, een betaling een
// POST. Je kon dus halverwege weglopen en een factuur achterlaten die niemand zo bedoeld had.
// Nu verzamelt het scherm je wijzigingen en gaan ze pas de deur uit als je op Opslaan drukt --
// Annuleren gooit ze weg, en weglopen met openstaande wijzigingen vraagt eerst wat je wilt.
//
// De regels, betalingen en notities die je ziet zijn daarom niet meer wat de server stuurde, maar dat
// mét je wijzigingen eroverheen. Dat rekenwerk staat hieronder in pure functies: geen scherm, geen
// server, dus los te controleren.

/** Eén nog niet opgeslagen wijziging. */
type Wijziging =
  | { soort: "regelToevoegen"; tijdelijkId: number; omschrijving: string; aantal: number; prijs: number; btwPercentage: number }
  | { soort: "regelWijzigen"; id: number; omschrijving: string; aantal: number; prijs: number; btwPercentage: number }
  | { soort: "regelVerwijderen"; id: number; label: string }
  | { soort: "betalingToevoegen"; tijdelijkId: number; bedrag: number; datum: string | null; betaalWijze: string; mailen: boolean }
  | { soort: "betalingVerwijderen"; id: number; label: string }
  | { soort: "notitie"; tekst: string }
  | { soort: "logToevoegen"; tijdelijkId: number; notitie: string; opmerking: string | null }
  | { soort: "logVerwijderen"; id: number; bron: string };

/**
 * Nieuwe rijen krijgen een eigen, negatief nummer.
 *
 * <p>Ze bestaan nog niet op de server, dus ze hebben nog geen echt nummer -- en negatief kan nooit
 * botsen met een bestaande rij, zodat "wijzig rij 12" niet per ongeluk een nieuwe rij raakt.
 */
export function isNieuweRij(id: number): boolean {
  return id < 0;
}

/** De regels zoals ze op het scherm horen te staan: wat de server stuurde, met de wijzigingen erover. */
export function regelsMetWijzigingen(basis: RegelRij[], wijzigingen: Wijziging[]): RegelRij[] {
  let uit = [...basis];
  for (const w of wijzigingen) {
    if (w.soort === "regelToevoegen") {
      uit = [...uit, {
        id: w.tijdelijkId, categorie: "", omschrijving: w.omschrijving, subOmschrijving: null,
        aantal: w.aantal, prijs: w.prijs, btwPercentage: w.btwPercentage,
        totaal: w.aantal * w.prijs,
      }];
    } else if (w.soort === "regelWijzigen") {
      uit = uit.map((r) => (r.id === w.id
        ? { ...r, omschrijving: w.omschrijving, aantal: w.aantal, prijs: w.prijs,
            btwPercentage: w.btwPercentage, totaal: w.aantal * w.prijs }
        : r));
    } else if (w.soort === "regelVerwijderen") {
      uit = uit.filter((r) => r.id !== w.id);
    }
  }
  return uit;
}

/** Idem voor de betalingen. */
export function betalingenMetWijzigingen(basis: BetalingRij[], wijzigingen: Wijziging[]): BetalingRij[] {
  let uit = [...basis];
  for (const w of wijzigingen) {
    if (w.soort === "betalingToevoegen") {
      uit = [...uit, {
        id: w.tijdelijkId, datum: w.datum, methode: w.betaalWijze, referentie: null,
        bedrag: w.bedrag, geannuleerd: false,
      }];
    } else if (w.soort === "betalingVerwijderen") {
      uit = uit.filter((b) => b.id !== w.id);
    }
  }
  return uit;
}

/** Twee decimalen, een keer afgerond -- anders stapelen centfouten op in het totaal. */
function centen(bedrag: number): number {
  return Math.round(bedrag * 100) / 100;
}

/**
 * De samenvatting zoals hij op het scherm hoort te staan: herrekend uit de regels en betalingen die
 * je nu ziet, inclusief wat nog niet is opgeslagen.
 *
 * <p>De server rekent de factuur na elke schrijfactie opnieuw door, maar zolang je wijzigingen nog in
 * het scherm staan is zijn getal dat van voor je bewerking. Je las dan honderd euro aan regels met
 * een totaal van nul eronder -- en erger: de controle "een losse verkoop moet op nul staan" keek naar
 * datzelfde oude getal en liet een factuur bewaren die wel open bleef staan (Maarten, 2026-10-24).
 *
 * <p>Prijzen zijn inclusief btw, net als op de server: de grondslag wordt per tarief teruggerekend
 * uit het inclusieve bedrag, zodat beide kanten op dezelfde cent uitkomen.
 */
export function samenvattingMetWijzigingen(
  basis: Samenvatting,
  regels: RegelRij[],
  betalingen: BetalingRij[],
): { samenvatting: Samenvatting; btwUitsplitsing: BtwRegel[] } {
  const inclusiefPerTarief = new Map<number, number>();
  for (const r of regels) {
    inclusiefPerTarief.set(r.btwPercentage, (inclusiefPerTarief.get(r.btwPercentage) ?? 0) + r.totaal);
  }
  const btwUitsplitsing: BtwRegel[] = [...inclusiefPerTarief.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([percentage, inclusief]) => {
      const grondslag = centen(inclusief / (1 + percentage / 100));
      return { percentage, grondslag, bedrag: centen(centen(inclusief) - grondslag) };
    });

  const totaal = centen(regels.reduce((som, r) => som + r.totaal, 0));
  // Een geannuleerde betaling is geen betaling: dat geld staat er niet, dus het mag de factuur niet
  // sluiten.
  const betaald = centen(betalingen.filter((b) => !b.geannuleerd).reduce((som, b) => som + b.bedrag, 0));
  return {
    samenvatting: {
      subtotaalExclBtw: centen(btwUitsplitsing.reduce((som, b) => som + b.grondslag, 0)),
      btwBedrag: centen(btwUitsplitsing.reduce((som, b) => som + b.bedrag, 0)),
      kortingBedrag: basis.kortingBedrag,
      totaal,
      betaald,
      openstaand: centen(totaal - betaald),
    },
    btwUitsplitsing,
  };
}

/** Voor wie de factuur komt, zolang hij nog niet bestaat. */
export interface NieuweFactuur {
  /** De klant waarvoor hij komt; null = losse verkoop, een factuur zonder debiteur. */
  klantId: number | null;
}

/** Het relatieantwoord, voor zover een factuur er iets aan heeft. */
interface RelatieVoorFactuur {
  id: number;
  klantNummer: number | null;
  organisatie: boolean;
  klantnaam: string | null;
  aanhef: string | null;
  voorletters: string | null;
  voornaam: string | null;
  tussenvoegsel: string | null;
  achternaam: string | null;
  naamBedrijf: string | null;
  emailAdres: string | null;
  telefoon1: string | null;
  telefoon2: string | null;
  straat: string | null;
  huisnummer: string | null;
  huisnummerToevoeging: string | null;
  postcode: string | null;
  woonplaats: string | null;
  land: string | null;
}

/** De relatie als klantblok, zodat een factuur die nog niet bestaat toch zijn klant laat zien. */
export function alsKlantGegevens(r: RelatieVoorFactuur | null | undefined): KlantGegevens | null {
  if (r == null) return null;
  return {
    id: r.id,
    naam: volledigeNaam(r),
    straat: r.straat, huisnummer: r.huisnummer, huisnummerToevoeging: r.huisnummerToevoeging,
    postcode: r.postcode, woonplaats: r.woonplaats, land: r.land,
    telefoon: r.telefoon1 ?? r.telefoon2,
    email: r.emailAdres,
    debiteurnummer: r.klantNummer == null ? null : String(r.klantNummer),
  };
}

/**
 * Een factuur die nog nergens staat.
 *
 * <p>"Nieuwe factuur" maakte er meteen een aan op de server. Een misklik -- of drie -- liet dus lege
 * concepten achter in de boekhouding (Maarten, 2026-10-24: "je kan pas een factuur aanmaken als er op
 * opslaan is gedrukt"). Nu begint het scherm met dit lege ontwerp en ontstaat de factuur pas bij
 * Opslaan, in een keer met je regels en betalingen erop.
 */
export function leegOntwerp(klant: KlantGegevens | null): FactuurDetailData {
  return {
    id: 0, nummer: null, factuurType: null, factuurDatum: todayIso(), vervalDatum: null,
    status: "CONCEPT", externeReferentie: null, opmerking: null, betaalmethode: null,
    klant,
    samenvatting: {
      subtotaalExclBtw: 0, btwBedrag: 0, kortingBedrag: 0, totaal: 0, betaald: 0, openstaand: 0,
    },
    btwUitsplitsing: [], regels: [], betalingen: [], tijdlijn: [], koppelingen: [],
  };
}

/** Idem voor het notitielogboek. */
export function notitiesMetWijzigingen(basis: NotitieRij[], wijzigingen: Wijziging[]): NotitieRij[] {
  let uit = [...basis];
  for (const w of wijzigingen) {
    if (w.soort === "logToevoegen") {
      uit = [{
        id: w.tijdelijkId, datum: null, melder: null, notitie: w.notitie,
        opmerking: w.opmerking, bron: "nieuw",
      }, ...uit];
    } else if (w.soort === "logVerwijderen") {
      uit = uit.filter((n) => n.id !== w.id);
    }
  }
  return uit;
}

/** De notitie op de factuur: de laatst getypte versie, of die van de server. */
export function notitieMetWijzigingen(basis: string | null, wijzigingen: Wijziging[]): string | null {
  let uit = basis;
  for (const w of wijzigingen) {
    if (w.soort === "notitie") {
      uit = w.tekst;
    }
  }
  return uit;
}

/**
 * Een wijziging erbij, waarbij een nieuwe de vorige over hetzelfde onderwerp vervangt.
 *
 * <p>Twee keer dezelfde regel bijwerken hoort één PUT te worden, niet twee. En een regel die je eerst
 * wijzigt en daarna weggooit hoeft ook niet eerst bijgewerkt te worden.
 */
export function metWijziging(bestaand: Wijziging[], nieuw: Wijziging): Wijziging[] {
  const zelfdeOnderwerp = (w: Wijziging): boolean => {
    if (nieuw.soort === "notitie") return w.soort === "notitie";
    if (nieuw.soort === "regelWijzigen") {
      return (w.soort === "regelWijzigen" && w.id === nieuw.id)
        || (w.soort === "regelToevoegen" && w.tijdelijkId === nieuw.id);
    }
    if (nieuw.soort === "regelVerwijderen") {
      return (w.soort === "regelWijzigen" && w.id === nieuw.id)
        || (w.soort === "regelToevoegen" && w.tijdelijkId === nieuw.id);
    }
    if (nieuw.soort === "betalingVerwijderen") {
      return w.soort === "betalingToevoegen" && w.tijdelijkId === nieuw.id;
    }
    if (nieuw.soort === "logVerwijderen") {
      return w.soort === "logToevoegen" && w.tijdelijkId === nieuw.id;
    }
    return false;
  };

  // Een nieuwe rij die je weer weggooit voordat je opslaat laat niets achter: niet toevoegen én
  // niet verwijderen, want op de server heeft hij nooit bestaan.
  const nieuweRijWeerWeg =
    (nieuw.soort === "regelVerwijderen" || nieuw.soort === "betalingVerwijderen" || nieuw.soort === "logVerwijderen")
    && isNieuweRij(nieuw.id);

  const over = bestaand.filter((w) => !zelfdeOnderwerp(w));
  if (nieuweRijWeerWeg) {
    return over;
  }
  // Een wijziging op een regel die in dezelfde ronde is toegevoegd blijft een toevoeging, anders
  // zou Opslaan een PUT doen op een nummer dat de server niet kent.
  if (nieuw.soort === "regelWijzigen" && isNieuweRij(nieuw.id)) {
    return [...over, {
      soort: "regelToevoegen", tijdelijkId: nieuw.id, omschrijving: nieuw.omschrijving,
      aantal: nieuw.aantal, prijs: nieuw.prijs, btwPercentage: nieuw.btwPercentage,
    }];
  }
  return [...over, nieuw];
}

const STATUS_TONE: Record<FactuurStatus, StatusTone> = {
  CONCEPT: "neutral",
  BETAALD: "success",
  OPENSTAAND: "warning",
  VERVALLEN: "danger",
};

// ---- small read-only presentation helpers ----------------------------------------------------------
// Maps a free-text payment-method string to its pill key — a faithful port of the Jmix
// FactuurBetalingenTile.methodeKey switch (substring matching, case-insensitive). Used to colour the
// betaling methode pill. Unknown methods fall back to the generic "ander" pill.
function payMethodKey(methode: string): string {
  const m = methode.toLowerCase();
  if (m.includes("ideal")) return "ideal";
  if (m.includes("pin") || m.includes("kaart")) return "pin";
  if (m.includes("contant") || m.includes("cash") || m.includes("kas")) return "contant";
  if (m.includes("incasso")) return "incasso";
  if (m.includes("over") || m.includes("bank") || m.includes("ibank") || m.includes("sepa")) return "bank";
  if (m.includes("credit") || m.includes("master") || m.includes("visa") || m.includes("amex")) return "creditcard";
  if (m.includes("paypal")) return "paypal";
  if (m.includes("tikkie")) return "tikkie";
  if (m.includes("niet voldaan") || m.includes("openstaand") || m.includes("onbekend")) return "open";
  return "ander";
}

// The 10 known category keys carried on RegelRij.categorie. Each maps to a colour swatch — the Jmix
// screen shows a per-line coloured category icon (10 colours + PNG illustrations); the PNGs can't be
// ported without assets, so React renders just the small coloured dot. Unknown/blank → "overig".
const CATEGORY_KEYS = [
  "pension",
  "creche",
  "asiel",
  "ambulance",
  "castratie",
  "vaccinatie",
  "medicatie",
  "behandeling",
  "voer",
  "product",
] as const;
function categorieSwatchKey(categorie: string | null): string {
  const c = (categorie ?? "").toLowerCase();
  return (CATEGORY_KEYS as readonly string[]).includes(c) ? c : "overig";
}

// Whole years between a birth date and today — the "(4 jaar)" suffix on the animal card.
function leeftijdInJaren(iso: string | null): number | null {
  if (!iso) return null;
  const born = new Date(iso);
  if (Number.isNaN(born.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - born.getFullYear();
  const beforeBirthday =
    now.getMonth() < born.getMonth() ||
    (now.getMonth() === born.getMonth() && now.getDate() < born.getDate());
  if (beforeBirthday) years -= 1;
  return years < 0 ? null : years;
}

/**
 * The counter's payment methods, as one-tap cards instead of a dropdown. The key is the free-text
 * string persisted on the payment (`betaling.betaal_wijze`) and the same string `payMethodKey` colours
 * the pill with, so what is registered here reads back identically on the payments table.
 */
const PAY_METHODS: { key: string; icon: React.ReactNode }[] = [
  { key: "Pin", icon: <CreditCard size={18} aria-hidden="true" /> },
  { key: "Contant", icon: <Banknote size={18} aria-hidden="true" /> },
  { key: "Overboeking", icon: <Landmark size={18} aria-hidden="true" /> },
  { key: "iDEAL", icon: <Smartphone size={18} aria-hidden="true" /> },
  { key: "Incasso", icon: <Repeat size={18} aria-hidden="true" /> },
];

function MetaRow({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="fawkes-fct-link__meta-row">
      {icon}
      <span>{children}</span>
    </div>
  );
}

export function FactuurDetail({
  id: bestaandeId,
  nieuw,
  onBack,
  onAangemaakt,
  onOpenRelatie,
  onOpenDier,
}: {
  /** Een factuur die er al is. Geen id en wel `nieuw` = een factuur die nog gemaakt moet worden. */
  id?: string;
  nieuw?: NieuweFactuur;
  onBack: () => void;
  /** De factuur is bij het eerste Opslaan ontstaan; het scherm eromheen mag naar zijn echte id. */
  onAangemaakt?: (factuurId: number) => void;
  onOpenRelatie?: (relatieId: string) => void;
  onOpenDier?: (dierId: string) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  // Zodra het eerste Opslaan de factuur heeft aangemaakt werkt dit scherm verder met dat echte id --
  // ook als het wegschrijven daarna halverwege misgaat, want dan staat die factuur er wel al en zou
  // een tweede poging anders een tweede factuur maken.
  const [gemaaktId, setGemaaktId] = useState<string | null>(null);
  const factuurId = bestaandeId ?? gemaaktId;
  /** Nog niets op de server: het scherm toont een leeg ontwerp en haalt niets op. */
  const ontwerp = factuurId == null;
  const id = factuurId ?? "nieuw";

  // The detail payload — relative url (the dataProvider prepends /api). Each write returns the refreshed
  // FactuurDetailResponse, but we re-fetch through Refine on success so the cache + every section stay
  // coherent (NFR2) and a conflict (409) lands us on fresh server state.
  const { data, isLoading, isError, refetch } = useCustom<FactuurDetailData>({
    url: `facturen/${id}`, method: "get", queryOptions: { enabled: !ontwerp },
  });
  // Bij een nieuwe factuur voor een klant staan zijn naam en adres er meteen op: de factuur bestaat
  // nog niet, dus de server kan ze niet meesturen, en een blok "geen klant" terwijl je hem net koos
  // ziet eruit als een fout.
  const { data: klantData } = useCustom<RelatieVoorFactuur>({
    url: `relaties/${nieuw?.klantId ?? 0}`,
    method: "get",
    queryOptions: { enabled: ontwerp && nieuw?.klantId != null },
  });
  const ontwerpBasis = useMemo(() => leegOntwerp(alsKlantGegevens(klantData?.data)), [klantData?.data]);
  const d = ontwerp ? ontwerpBasis : data?.data;

  // Write surface (Epic 3) — only offered to ROLE_FACTUUR_EDIT holders; the endpoints are also dark
  // server-side behind fawkes.api.factuur.write.enabled until the docaserver coexistence smoke test passes.
  const { data: permissions } = usePermissions<string[]>();
  const canEdit = Array.isArray(permissions) && permissions.includes("ROLE_FACTUUR_EDIT");
  // Lifecycle actions (credit/duplicate/cancel) gate on the separate ROLE_FACTUUR_LIFECYCLE (Q1 split).
  const canLifecycle = Array.isArray(permissions) && permissions.includes("ROLE_FACTUUR_LIFECYCLE");
  const { mutate: mutateWrite, mutateAsync: schrijfAsync, isLoading: writing } = useCustomMutation();

  // Wat er nog niet is opgeslagen. Leeg = er valt niets te bewaren en niets te annuleren.
  const [wijzigingen, setWijzigingen] = useState<Wijziging[]>([]);
  const [opslaanBezig, setOpslaanBezig] = useState(false);

  const [verlatenVraag, setVerlatenVraag] = useState(false);
  const volgendTijdelijkId = useRef(-1);
  const nieuwTijdelijkId = () => volgendTijdelijkId.current--;
  const voegToe = (w: Wijziging) => setWijzigingen((eerder) => metWijziging(eerder, w));
  const ietsTeBewaren = wijzigingen.length > 0;

  // Het tabblad sluiten of verversen is de andere manier om weg te lopen; de browser vraagt dan zelf
  // om bevestiging. De tekst komt van de browser -- die van ons wordt genegeerd -- dus het gaat er
  // alleen om dát de vraag komt.
  useEffect(() => {
    if (!ietsTeBewaren) {
      return;
    }
    const waarschuw = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", waarschuw);
    return () => window.removeEventListener("beforeunload", waarschuw);
  }, [ietsTeBewaren]);


  // post-write notice: 409 = changed-elsewhere (reload + retry), 403 = forbidden, else generic error.
  // A lifecycle success surfaces an informational notice (credit/duplicate created — with the new id).
  const [notice, setNotice] = useState<
    | "conflict"
    | "forbidden"
    | "error"
    | "emailSent"
    | "emailNoEmail"
    | "exportError"
    | "geenRegels"
    | "losseVerkoopOnbalans"
    | { kind: "credited" | "duplicated"; factuurId: number }
    | null
  >(null);

  // dialog + inline-edit state
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [lineId, setLineId] = useState<number | null>(null);
  const [editLineOpen, setEditLineOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [noteEditing, setNoteEditing] = useState(false);
  // Waar de factuur naartoe gaat als er geen klant aan hangt (losse verkoop). Voorgevuld met het adres
  // van de klant als dat er wel is, zodat het bevestigingsvenster niets wegneemt van wat er al was.
  const [mailNaar, setMailNaar] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [historieOpen, setHistorieOpen] = useState(false);
  const [confirm, setConfirm] = useState<
    | { kind: "herinnering" | "crediteren" | "dupliceren" | "annuleren" | "email" }
    | { kind: "regel" | "betaling" | "notitielog"; id: number; label: string; bron?: string }
    | null
  >(null);
  // payment form
  const [payBedrag, setPayBedrag] = useState("");
  const [payDatum, setPayDatum] = useState(() => todayIso());
  const [payWijze, setPayWijze] = useState("Pin");
  const [payEmail, setPayEmail] = useState(true);
  // Edit form for one existing line, prefilled from the row.
  const [lineNaam, setLineNaam] = useState("");
  const [lineAantal, setLineAantal] = useState("1");
  const [linePrijs, setLinePrijs] = useState("");
  const [lineBtw, setLineBtw] = useState("21");
  // note draft (the single `opmerking` memo)
  const [noteDraft, setNoteDraft] = useState("");

  // Notes-log (Epic 6 part 2) — the dated per-type log, distinct from the `opmerking` memo above.
  // RELATIVE url (the dataProvider prepends /api). An empty list means either "no entries yet" or
  // "this invoice has no per-type wrapper"; the section shows a muted hint either way.
  const { data: notitiesData, refetch: refetchNotities } = useCustom<NotitieRij[]>({
    url: `facturen/${id}/notities`,
    method: "get",
    queryOptions: { enabled: !ontwerp },
  });
  const serverNotities = notitiesData?.data ?? [];
  const [logAddOpen, setLogAddOpen] = useState(false);
  const [logFout, setLogFout] = useState<string | null>(null);
  const [logNotitie, setLogNotitie] = useState("");

  // The linked animal's photo — fetched authenticated as a blob (the bearer can't ride an <img src>),
  // exactly like the animal overviews. Declared before the loading guards so the hook order is stable.
  const koppelingen = data?.data?.koppelingen ?? [];
  const dierKoppeling = koppelingen.find((k) => k.type === "DIER");
  const photoUrls = useAnimalPhotoUrls(dierKoppeling?.dierId != null ? [dierKoppeling.dierId] : []);

  // The overflow menu closes on Escape and on any click outside it.
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [menuOpen]);

  if (!ontwerp && isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (isError || !d) return <p className="fawkes-error">{t("factuurDetailView.loadFailed")}</p>;

  // Wat je ziet is wat de server heeft, met je eigen wijzigingen eroverheen -- anders zou een regel
  // die je net toevoegde pas na Opslaan verschijnen en lijkt het scherm kapot.
  const regels = regelsMetWijzigingen(d.regels, wijzigingen);
  const betalingen = betalingenMetWijzigingen(d.betalingen, wijzigingen);
  const notities = notitiesMetWijzigingen(serverNotities, wijzigingen);
  const notitieTekst = notitieMetWijzigingen(d.opmerking, wijzigingen);
  // ... en dat geldt net zo goed voor de bedragen: subtotaal, btw, betaald en openstaand komen uit de
  // regels en betalingen hierboven, niet uit de laatste doorrekening van de server.
  const { samenvatting: sam, btwUitsplitsing: btwLive } =
    samenvattingMetWijzigingen(d.samenvatting, regels, betalingen);

  /**
   * Een losse verkoop die niet klopt: een factuur zonder debiteur waar nog iets van openstaat.
   *
   * <p>Er is niemand om een restant aan te factureren, dus hij moet aan de toonbank op nul uitkomen.
   * De som gaat over wat je nu ziet -- regels en betalingen inclusief wat nog niet is opgeslagen --
   * en niet over het laatste getal van de server: dat is van voor je bewerking, en daardoor liet deze
   * controle een factuur door die wel degelijk open bleef staan (Maarten, 2026-10-24).
   *
   * <p>Centen vergelijken en geen exacte nul: een totaal is een optelling van afgeronde regels, en
   * een halve cent verschil mag geen blokkade zijn die niemand kan oplossen.
   */
  const zonderDebiteur = ontwerp ? nieuw?.klantId == null : d.klant == null;
  const losseVerkoopOnbalans = zonderDebiteur && Math.abs(sam.openstaand) >= 0.005;

  // Een concept staat op nul omdat er nog niets op staat, niet omdat er betaald is.
  const volledigBetaald = d.status !== "CONCEPT" && sam.openstaand <= 0;
  // Een concept zonder regels is nog geen factuur: je kunt hem niet bewaren, en de uitweg is weggooien
  // of regels toevoegen. Zonder die uitweg sta je op een scherm met twee grijze knoppen (Maarten, 2026-10-23).
  const leegConcept = d.status === "CONCEPT" && regels.length === 0;
  // Every write returns the refreshed FactuurDetailResponse; we refetch through Refine on success so the
  // query cache and all sections stay coherent. On error we surface a notice — a 409 means the invoice was
  // changed elsewhere, so we also reload to land on fresh server state before the user retries.
  const runWrite = (
    url: string,
    method: "post" | "put" | "delete",
    values?: Record<string, unknown>,
  ) =>
    mutateWrite(
      { url, method, values: values ?? {}, successNotification: false, errorNotification: false },
      {
        onSuccess: () => {
          setNotice(null);
          refetch();
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          if (code === 409) {
            setNotice("conflict");
            refetch();
          } else if (code === 403) {
            setNotice("forbidden");
          } else {
            setNotice("error");
          }
        },
      },
    );

  // Lifecycle write (Epic 4) — credit/duplicate return { factuurId } (no navigation prop exists, so we
  // surface a notice with the new id + refetch the current detail); cancel returns the refreshed detail.
  const runLifecycle = (action: "crediteren" | "dupliceren" | "annuleren") =>
    mutateWrite(
      {
        url: `facturen/${id}/${action === "crediteren" ? "credit" : action === "dupliceren" ? "duplicate" : "cancel"}`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          if (action === "annuleren") {
            setNotice(null);
          } else {
            const newId = (res?.data as { factuurId?: number } | undefined)?.factuurId ?? 0;
            setNotice({ kind: action === "crediteren" ? "credited" : "duplicated", factuurId: newId });
          }
          refetch();
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          if (code === 409) {
            setNotice("conflict");
            refetch();
          } else if (code === 403) {
            setNotice("forbidden");
          } else {
            setNotice("error");
          }
        },
      },
    );

  // Opens on the outstanding balance and today's date: settling in full at the counter is the normal
  // case, so the operator confirms rather than retypes what the screen already knows.
  const hasKlantEmail = (d.klant?.email ?? "").trim().length > 0;
  const openPayment = () => {
    setPayBedrag(sam.openstaand > 0 ? String(sam.openstaand) : "");
    setPayDatum(todayIso());
    setPayEmail(hasKlantEmail);
    setPaymentOpen(true);
  };
  // No paper at the counter: the customer gets the invoice by e-mail, with this payment already on it.
  // The send is chained onto the payment so the PDF is generated from the settled invoice, never from
  // the state before it; it only fires when the operator left the box ticked.
  const submitPayment = () => {
    voegToe({
      soort: "betalingToevoegen",
      tijdelijkId: nieuwTijdelijkId(),
      bedrag: Number(payBedrag),
      datum: payDatum || null,
      betaalWijze: payWijze,
      // De mail hangt aan déze betaling: hij gaat pas de deur uit als die is weggeschreven, zodat de
      // pdf de betaalde factuur laat zien en niet de stand van ervoor.
      mailen: payEmail && hasKlantEmail,
    });
    setPaymentOpen(false);
  };

  // Adding a line goes through the shared ProductTariefPicker (the same catalog search the wizards use).
  // It hands back the composed line with the product's own BTW, or — when the product or tariff has
  // none — the percentage the user had to choose in the picker. No silent 21% any more: the api now
  // rejects a line without BTW, because that default put 21% on tariffs that should have been 9% or 0%.
  const addLineFromPicker = (regel: GekozenProductRegel) =>
    voegToe({
      soort: "regelToevoegen",
      tijdelijkId: nieuwTijdelijkId(),
      omschrijving: regel.omschrijving,
      aantal: regel.aantal,
      prijs: regel.prijsInclBtw,
      btwPercentage: regel.btwPercentage,
    });
  const openBewerkRegel = (r: RegelRij) => {
    setLineId(r.id);
    setLineNaam(r.omschrijving);
    setLineAantal(String(r.aantal));
    setLinePrijs(String(r.prijs));
    setLineBtw(String(r.btwPercentage));
    setEditLineOpen(true);
  };
  const submitLine = () => {
    if (lineId == null) return;
    voegToe({
      soort: "regelWijzigen",
      id: lineId,
      omschrijving: lineNaam.trim(),
      aantal: Number(lineAantal),
      prijs: Number(linePrijs),
      btwPercentage: Number(lineBtw),
    });
    setEditLineOpen(false);
  };
  const saveNote = () => {
    voegToe({ soort: "notitie", tekst: noteDraft });
    setNoteEditing(false);
  };

  // Notes-log writes refetch only the log (not the whole detail); same notice handling as runWrite.
  // Het venster blijft open tot de notitie echt bewaard is; gaat het mis, dan staat de melding in het venster
  // en niet bovenaan de pagina buiten beeld, en is wat er getypt was niet weg (Maarten, 2026-10-14).
  const submitNoteLog = () => {
    setLogFout(null);
    voegToe({
      soort: "logToevoegen",
      tijdelijkId: nieuwTijdelijkId(),
      notitie: logNotitie.trim(),
      opmerking: null,
    });
    setLogNotitie("");
    setLogAddOpen(false);
  };

  // E-mail the invoice PDF to the customer (Epic 4) — an outward SEND gated on ROLE_FACTUUR_EDIT and the
  // server write flag. A 422 means the customer has no e-mail on file; success surfaces an info notice.
  const sendEmail = (naar?: string, doel: string = id) =>
    mutateWrite(
      {
        url: `facturen/${doel}/email`,
        method: "post",
        // Leeg betekent "naar de klant van de factuur"; een losse verkoop heeft die niet, dus daar
        // vraagt het scherm eerst om een adres.
        values: naar && naar.trim() ? { email: naar.trim() } : {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => setNotice("emailSent"),
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          if (code === 422) setNotice("emailNoEmail");
          else if (code === 403) setNotice("forbidden");
          else setNotice("error");
        },
      },
    );

  /** Eén wijziging omzetten naar de aanroep die hem wegschrijft. */
  const alsAanroep = (w: Wijziging, doel: string): { url: string; method: "post" | "put" | "delete"; values: Record<string, unknown> } => {
    switch (w.soort) {
      case "regelToevoegen":
        return { url: `facturen/${doel}/regels`, method: "post",
          values: { productNaam: w.omschrijving, aantal: w.aantal, prijs: w.prijs, btwPercentage: w.btwPercentage } };
      case "regelWijzigen":
        return { url: `facturen/${doel}/regels/${w.id}`, method: "put",
          values: { productNaam: w.omschrijving, aantal: w.aantal, prijs: w.prijs, btwPercentage: w.btwPercentage } };
      case "regelVerwijderen":
        return { url: `facturen/${doel}/regels/${w.id}`, method: "delete", values: {} };
      case "betalingToevoegen":
        return { url: `facturen/${doel}/betalingen`, method: "post",
          values: { bedrag: w.bedrag, datum: w.datum, betaalWijze: w.betaalWijze } };
      case "betalingVerwijderen":
        return { url: `facturen/${doel}/betalingen/${w.id}`, method: "delete", values: {} };
      case "notitie":
        return { url: `facturen/${doel}/notitie`, method: "put", values: { notitie: w.tekst } };
      case "logToevoegen":
        return { url: `facturen/${doel}/notities`, method: "post",
          values: { notitie: w.notitie, opmerking: w.opmerking } };
      case "logVerwijderen":
        return { url: `facturen/${doel}/notities/${w.id}${w.bron === "nieuw" ? "?bron=nieuw" : ""}`,
          method: "delete", values: {} };
    }
  };

  /**
   * Alles wegschrijven, in de volgorde waarin je het deed.
   *
   * <p>Eén voor één en niet tegelijk: de factuur wordt bij elke schrijfactie op de server herrekend, en
   * twee aanroepen die elkaar kruisen leveren een 409 op. Gaat er één mis, dan stopt het daar en blijven
   * die wijziging en alles erna staan -- dan kun je het probleem oplossen en opnieuw opslaan zonder dat
   * je werk weg is, en is wat al wél gelukt is niet twee keer verwerkt.
   */
  const opslaan = async () => {
    setOpslaanBezig(true);
    setNotice(null);
    let doel = factuurId;
    let rest = [...wijzigingen];
    try {
      // Hier ontstaat de factuur, en geen moment eerder: pas nu is er iets om te bewaren. Lukt het
      // wegschrijven daarna niet, dan blijft dit id staan -- een tweede poging vult dan dezelfde
      // factuur aan in plaats van er nog een achter te laten.
      if (doel == null) {
        const res = await schrijfAsync(nieuw?.klantId == null
          ? { url: "losse-verkoop/nieuw", method: "post", values: {},
              successNotification: false, errorNotification: false }
          : { url: "facturen", method: "post", values: { klantId: nieuw.klantId },
              successNotification: false, errorNotification: false });
        const verseId = (res?.data as { factuurId?: number } | undefined)?.factuurId;
        if (verseId == null) throw new Error("factuur niet aangemaakt");
        doel = String(verseId);
        setGemaaktId(doel);
      }
      for (const w of wijzigingen) {
        const aanroep = alsAanroep(w, doel);
        await schrijfAsync({ ...aanroep, successNotification: false, errorNotification: false });
        rest = rest.slice(1);
        // De mail hangt aan deze betaling en gaat er pas achteraan, zodat de pdf de betaalde factuur
        // toont en niet de stand van ervoor.
        if (w.soort === "betalingToevoegen" && w.mailen) {
          sendEmail(undefined, doel);
        }
      }
      setWijzigingen([]);
      // Hij bestaat nu: het scherm eromheen mag naar zijn eigen adres, zodat terug, verversen en de
      // knoppen die een factuurnummer nodig hebben weer kloppen.
      if (ontwerp) onAangemaakt?.(Number(doel));
    } catch (err) {
      setWijzigingen(rest);
      const code = (err as { statusCode?: number }).statusCode;
      if (code === 409) setNotice("conflict");
      else if (code === 403) setNotice("forbidden");
      else setNotice("error");
    } finally {
      setOpslaanBezig(false);
      refetch();
      refetchNotities();
    }
  };

  /**
   * Een leeg concept bestaat alleen omdat "Nieuwe factuur" hem aanmaakte; annuleren hoort hem dan ook
   * weg te halen en je terug te brengen. Het factuurnummer gaat niet verloren -- de server leidt het
   * volgende nummer af uit het hoogste dat er is, dus zodra deze rij weg is komt het weer vrij.
   */
  const gooiLeegConceptWeg = async () => {
    // Een ontwerp bestaat alleen in dit scherm: er valt niets weg te gooien, je loopt er gewoon weg.
    if (ontwerp) {
      onBack();
      return;
    }
    setOpslaanBezig(true);
    setNotice(null);
    try {
      await schrijfAsync({
        url: `facturen/${id}`, method: "delete", values: {},
        successNotification: false, errorNotification: false,
      });
      onBack();
    } catch {
      // Lukt het weggooien niet, dan blijft het concept staan en zeggen we dat; stil teruggaan zou een
      // factuur achterlaten waarvan je denkt dat hij weg is.
      setNotice("error");
    } finally {
      setOpslaanBezig(false);
    }
  };

  /** Alles weggooien wat nog niet is opgeslagen, en terug naar wat de server heeft. */
  const annuleren = () => {
    setWijzigingen([]);
    setNotice(null);
    setNoteEditing(false);
    refetch();
    refetchNotities();
  };

  // Authenticated binary downloads (the bearer can't ride a plain <a href>); a failure surfaces a notice.
  const exportPdf = () => downloadFactuurPdf(id, d.nummer ?? id).catch(() => setNotice("exportError"));
  const exportCsv = () => downloadBetalingenCsv(id, d.nummer ?? id).catch(() => setNotice("exportError"));

  const runConfirm = () => {
    if (!confirm) return;
    if (confirm.kind === "herinnering") runWrite(`facturen/${id}/herinneringen`, "post");
    else if (confirm.kind === "crediteren" || confirm.kind === "dupliceren" || confirm.kind === "annuleren")
      runLifecycle(confirm.kind);
    else if (confirm.kind === "email") sendEmail(mailNaar);
    else if (confirm.kind === "regel") {
      voegToe({ soort: "regelVerwijderen", id: confirm.id, label: confirm.label ?? "" });
    } else if (confirm.kind === "betaling") {
      voegToe({ soort: "betalingVerwijderen", id: confirm.id, label: confirm.label ?? "" });
    }
    else if (confirm.kind === "notitielog") {
      voegToe({ soort: "logVerwijderen", id: confirm.id, bron: confirm.bron ?? "" });
    }
    setConfirm(null);
  };

  const paymentValid = Number(payBedrag) > 0 && payWijze.trim().length > 0;
  // Negative would mean overpaying; the row reads 0,00 rather than "-€ 20,00", which is not a debt.
  const restantNaBetaling = Math.max(0, sam.openstaand - (Number(payBedrag) || 0));
  const lineValid = lineNaam.trim().length > 0 && linePrijs.trim().length > 0 && !Number.isNaN(Number(linePrijs));
  const herinneringen = d.tijdlijn.filter((e) => e.type === "HERINNERING").length;

  // "Betaald op" / "Betaald via" in the status box come from the most recent payment that still counts.
  const laatsteBetaling = betalingen.filter((b) => !b.geannuleerd).at(-1) ?? null;

  const adresRegels = d.klant
    ? [
        [d.klant.straat, d.klant.huisnummer, d.klant.huisnummerToevoeging].filter(Boolean).join(" ").trim(),
        [d.klant.postcode, d.klant.woonplaats].filter(Boolean).join("  ").trim(),
        d.klant.land ?? "",
      ].filter((s) => s.length > 0)
    : [];

  const verblijfKoppeling = koppelingen.find((k) => k.type === "VERBLIJF");
  const ambulanceKoppeling = koppelingen.find((k) => k.type === "AMBULANCE");
  const dierFoto = dierKoppeling?.dierId != null ? photoUrls[String(dierKoppeling.dierId)] : undefined;
  const leeftijd = leeftijdInJaren(dierKoppeling?.geboortedatum ?? null);

  return (
    <div className="fawkes-fct">
      {/* Terug staat bovenaan en gaat naar het scherm waar je vandaan kwam (Maarten, 2026-10-14); het kruisje deed
          hetzelfde en is weg. */}
      <button type="button" className="fawkes-fct__terug"
        onClick={() => (ietsTeBewaren ? setVerlatenVraag(true) : onBack())}>
        <ArrowLeft size={16} aria-hidden="true" />
        {t("factuurDetailView.terugNaarOverzicht")}
      </button>
      <header className="fawkes-fct__header">
        <div>
          <div className="fawkes-fct__title">
            <h1>
              {t("factuurDetailView.heading")} {d.nummer ?? t("factuurDetailView.nogGeenNummer")}
            </h1>
            {/* Geen chip bij een concept: elke nieuwe factuur is concept, dus het label voegt niets toe
                naast het STATUS-blok verderop. Betaald/Openstaand/Vervallen zeggen wel iets en blijven. */}
            {d.status !== "CONCEPT" && (
              <StatusBadge tone={STATUS_TONE[d.status]}>{t(`factuurDetailView.status.${d.status}`)}</StatusBadge>
            )}
          </div>
          <p className="fawkes-fct__sub">{t("factuurDetailView.subtitle")}</p>
        </div>
        {/* Afdrukken, mailen, crediteren en het logboek gaan over een factuur die er is. Op een
            ontwerp is er niets om af te drukken of te versturen; die knoppen zouden alleen maar een
            foutmelding kunnen geven. */}
        {!ontwerp && (
        <div className="fawkes-fct__actions">
          {/* Exports are reads — offered to anyone who can view the invoice. */}
          <Button variant="secondary" onClick={exportPdf}>
            <Download size={16} aria-hidden="true" />
            {t("factuurDetailView.export.pdf")}
          </Button>
          {/* E-mail is an outward send — gated on ROLE_FACTUUR_EDIT, behind a confirm. */}
          {/* Naar buiten toe niets versturen zolang het scherm en de server uit elkaar lopen: de pdf
              zou de factuur van vóór je wijzigingen laten zien. */}
          <Button variant="secondary" disabled={!canEdit || writing || ietsTeBewaren}
            title={ietsTeBewaren ? t("factuurDetailView.concept.actieGeblokkeerd") : undefined}
            onClick={() => { setMailNaar((d.klant?.email ?? "").trim()); setConfirm({ kind: "email" }); }}>
            <Send size={16} aria-hidden="true" />
            {t("factuurDetailView.export.email")}
          </Button>
          {/* The history is a look-up, not part of the document — it opens from here instead of
              trailing the page, where it pushed the real content up. */}
          <Button variant="secondary" onClick={() => setHistorieOpen(true)}>
            <HistoryIcon size={16} aria-hidden="true" />
            {t("factuurDetailView.acties.historie")}
          </Button>
          {/* The design's "⋯": the actions that are rarely used or destructive live behind it. */}
          <div className="fawkes-fct__menu-wrap" ref={menuRef}>
            <Button
              variant="secondary"
              className="fawkes-fct__icon-btn"
              aria-label={t("factuurDetailView.acties.meer")}
              title={t("factuurDetailView.acties.meer")}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <MoreHorizontal size={16} aria-hidden="true" />
            </Button>
            {menuOpen && (
              <div className="fawkes-fct__menu" role="menu">
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); exportCsv(); }}>
                  {t("factuurDetailView.export.csv")}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!canEdit || writing || ietsTeBewaren || herinneringen >= 2}
                  title={ietsTeBewaren
                    ? t("factuurDetailView.concept.actieGeblokkeerd")
                    : herinneringen >= 2 ? t("factuurDetailView.acties.herinneringMax") : undefined}
                  onClick={() => { setMenuOpen(false); setConfirm({ kind: "herinnering" }); }}
                >
                  {t("factuurDetailView.acties.herinnering")}
                </button>
                {/* Annuleren staat er niet meer in: het legacy-statusveld kent geen "geannuleerd",
                    dus de actie stempelde alleen een regel in de opmerking en zette het openstaande
                    bedrag op nul. Aan het scherm veranderde niets, en dus leek hij kapot. */}
                {(["crediteren", "dupliceren"] as const).map((a) => (
                  <button
                    key={a}
                    type="button"
                    role="menuitem"
                    disabled={!canLifecycle || writing || ietsTeBewaren}
                    title={ietsTeBewaren ? t("factuurDetailView.concept.actieGeblokkeerd") : undefined}
                    onClick={() => { setMenuOpen(false); setConfirm({ kind: a }); }}
                  >
                    {t(`factuurDetailView.acties.${a}`)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </header>

      {notice && (
        <div className="fawkes-factuur-notice" role="status">
          {typeof notice === "string"
            ? t(`factuurDetailView.notice.${notice}`)
            : t(`factuurDetailView.notice.${notice.kind}`, { args: [notice.factuurId] })}
        </div>
      )}

      {/* What the invoice is about. Only rendered when the invoice actually has a shelter stay behind it —
          purchase invoices and wizard concept invoices have nothing to link. There is deliberately no
          "koppelen" or "verwijderen" affordance: the link lives on the legacy shelter record, and
          re-pointing it from here would rewrite the stay, not the invoice. */}
      {koppelingen.length > 0 && (
        <section className="fawkes-fct-section">
          <div className="fawkes-fct-section__head">
            <h2>
              {t("factuurDetailView.sectie.koppelingen")} ({koppelingen.length})
            </h2>
          </div>
          <div className="fawkes-fct-links">
            {dierKoppeling && (
              <article className="fawkes-fct-link">
                <div className="fawkes-fct-link__media">
                  {dierFoto ? (
                    <img src={dierFoto} alt="" />
                  ) : (
                    <PawPrint size={28} aria-hidden="true" className="fawkes-fct-link__placeholder" />
                  )}
                </div>
                <h3 className="fawkes-fct-link__title">{dierKoppeling.naam ?? "—"}</h3>
                <div className="fawkes-fct-link__meta">
                  {dierKoppeling.chipnummer && (
                    <MetaRow icon={<CreditCard size={14} aria-hidden="true" />}>{dierKoppeling.chipnummer}</MetaRow>
                  )}
                  {dierKoppeling.diersoort && (
                    <MetaRow icon={<PawPrint size={14} aria-hidden="true" />}>{dierKoppeling.diersoort}</MetaRow>
                  )}
                  {dierKoppeling.geslacht && (
                    <MetaRow icon={<PawPrint size={14} aria-hidden="true" />}>{dierKoppeling.geslacht}</MetaRow>
                  )}
                  {dierKoppeling.geboortedatum && (
                    <MetaRow icon={<Calendar size={14} aria-hidden="true" />}>
                      {formatDate(dierKoppeling.geboortedatum, locale)}
                      {leeftijd != null && ` (${t("factuurDetailView.koppeling.jaar", { args: [leeftijd] })})`}
                    </MetaRow>
                  )}
                </div>
                {onOpenDier && dierKoppeling.dierId != null && (
                  <div className="fawkes-fct-link__actions">
                    <Button variant="secondary" onClick={() => onOpenDier(String(dierKoppeling.dierId))}>
                      <ExternalLink size={14} aria-hidden="true" />
                      {t("factuurDetailView.koppeling.bekijkDier")}
                    </Button>
                  </div>
                )}
              </article>
            )}

            {verblijfKoppeling && (
              <article className="fawkes-fct-link">
                <div className="fawkes-fct-link__media">
                  <Home size={28} aria-hidden="true" className="fawkes-fct-link__placeholder" />
                </div>
                <h3 className="fawkes-fct-link__title">
                  {verblijfKoppeling.verblijfNummer != null
                    ? t("factuurDetailView.koppeling.verblijfNr", { args: [verblijfKoppeling.verblijfNummer] })
                    : t("factuurDetailView.koppeling.verblijf")}
                </h3>
                <div className="fawkes-fct-link__meta">
                  {verblijfKoppeling.opnameDatum && (
                    <MetaRow icon={<Calendar size={14} aria-hidden="true" />}>
                      {t("factuurDetailView.koppeling.opname")}: {formatDate(verblijfKoppeling.opnameDatum, locale)}
                    </MetaRow>
                  )}
                  {verblijfKoppeling.vertrekDatum && (
                    <MetaRow icon={<Calendar size={14} aria-hidden="true" />}>
                      {t("factuurDetailView.koppeling.vertrek")}: {formatDate(verblijfKoppeling.vertrekDatum, locale)}
                    </MetaRow>
                  )}
                </div>
              </article>
            )}

            {ambulanceKoppeling && (
              <article className="fawkes-fct-link">
                <div className="fawkes-fct-link__media">
                  <Truck size={28} aria-hidden="true" className="fawkes-fct-link__placeholder" />
                </div>
                <h3 className="fawkes-fct-link__title">{ambulanceKoppeling.naam}</h3>
                <div className="fawkes-fct-link__meta">
                  {ambulanceKoppeling.ritnummer && (
                    <MetaRow icon={<Truck size={14} aria-hidden="true" />}>
                      {t("factuurDetailView.koppeling.ritnummer")}: {ambulanceKoppeling.ritnummer}
                    </MetaRow>
                  )}
                  {ambulanceKoppeling.opnameDatum && (
                    <MetaRow icon={<Calendar size={14} aria-hidden="true" />}>
                      {formatDate(ambulanceKoppeling.opnameDatum, locale)}
                    </MetaRow>
                  )}
                </div>
              </article>
            )}
          </div>
        </section>
      )}

      {/* Customer / invoice facts / payment status, three columns as in the design. */}
      <section className="fawkes-fct-section">
        <div className="fawkes-fct-meta">
          <div className="fawkes-fct-meta__col">
            <div>
              {/* Een factuur zonder debiteur is een losse verkoop aan de balie. Dat is een soort
                  factuur en geen ontbrekend gegeven, dus staat er wat het is in plaats van een
                  mededeling dat er iets mist (Maarten, 2026-10-23). */}
              <p className="fawkes-fct-meta__label">
                {d.klant ? t("factuurDetailView.klant.naam") : t("factuurDetailView.klant.soort")}
              </p>
              <p className="fawkes-fct-meta__value">
                {d.klant?.naam ?? t("factuurDetailView.klant.losseVerkoop")}
              </p>
              {!d.klant && (
                <p className="fawkes-fct-meta__sub">{t("factuurDetailView.klant.geenDebiteur")}</p>
              )}
            </div>
            {adresRegels.length > 0 && (
              <div className="fawkes-fct-meta__lines">
                {adresRegels.map((r) => (
                  <span key={r}>{r}</span>
                ))}
              </div>
            )}
            {d.klant?.email && (
              <div className="fawkes-fct-meta__contact">
                <Mail size={14} aria-hidden="true" />
                {d.klant.email}
              </div>
            )}
            {d.klant?.telefoon && (
              <div className="fawkes-fct-meta__contact">
                <Phone size={14} aria-hidden="true" />
                {d.klant.telefoon}
              </div>
            )}
            {d.klant?.debiteurnummer && (
              <div className="fawkes-fct-meta__contact">
                <CreditCard size={14} aria-hidden="true" />
                {t("factuurDetailView.klant.debiteur")}: {d.klant.debiteurnummer}
              </div>
            )}
            {onOpenRelatie && d.klant?.id != null && (
              <Button
                variant="secondary"
                className="fawkes-fct-meta__link"
                onClick={() => onOpenRelatie(String(d.klant?.id))}
              >
                <ExternalLink size={14} aria-hidden="true" />
                {t("factuurDetailView.klant.bekijk")}
              </Button>
            )}
          </div>

          <div className="fawkes-fct-meta__col">
            <div>
              <p className="fawkes-fct-meta__label">{t("factuurDetailView.info.nummer")}</p>
              <p className="fawkes-fct-meta__value">{d.nummer ?? t("factuurDetailView.nogGeenNummer")}</p>
            </div>
            <div>
              <p className="fawkes-fct-meta__label">{t("factuurDetailView.info.datum")}</p>
              <p className="fawkes-fct-meta__value">{formatDate(d.factuurDatum, locale)}</p>
            </div>
            <div>
              <p className="fawkes-fct-meta__label">{t("factuurDetailView.info.verval")}</p>
              <p className="fawkes-fct-meta__value">{formatDate(d.vervalDatum, locale)}</p>
            </div>
            <div>
              <p className="fawkes-fct-meta__label">{t("factuurDetailView.info.referentie")}</p>
              <p className="fawkes-fct-meta__value">{d.externeReferentie ?? "—"}</p>
            </div>
          </div>

          <div className="fawkes-fct-meta__col">
            <div className={`fawkes-fct-status fawkes-fct-status--${STATUS_TONE[d.status]}`}>
              <div className="fawkes-fct-status__row">
                <p className="fawkes-fct-meta__label">{t("factuurDetailView.info.status")}</p>
                <StatusBadge tone={STATUS_TONE[d.status]}>{t(`factuurDetailView.status.${d.status}`)}</StatusBadge>
              </div>
              <div className="fawkes-fct-status__row">
                <p className="fawkes-fct-meta__label">{t("factuurDetailView.betaald.op")}</p>
                <p className="fawkes-fct-status__value">
                  {laatsteBetaling ? formatDate(laatsteBetaling.datum, locale) : "—"}
                </p>
              </div>
              <div className="fawkes-fct-status__row">
                <p className="fawkes-fct-meta__label">{t("factuurDetailView.betaald.via")}</p>
                <p className="fawkes-fct-status__value">{d.betaalmethode ?? "—"}</p>
              </div>
              <div className="fawkes-fct-status__row">
                <p className="fawkes-fct-meta__label">{t("factuurDetailView.betaalstatus.openstaandLabel")}</p>
                <p className="fawkes-fct-status__value">{formatEuro(sam.openstaand, locale)}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Invoice lines. There is no unit column: `factuur_regel` stores no unit, and printing a fixed
          "st" would be wrong for the day-based pension and shelter lines. */}
      <section className="fawkes-fct-section">
        <div className="fawkes-fct-table-wrap">
          <IndeelbareTabel
            tabelKey="factuur-regels"
            columns={[
              {
                key: "omschrijving", header: t("factuurDetailView.regels.col.omschrijving"),
                cell: (r) => (
                  <div className="fawkes-fct-item">
                    <span
                      className={`fawkes-factuur-regel__swatch fawkes-factuur-regel__swatch--${categorieSwatchKey(r.categorie)}`}
                      aria-hidden="true"
                    />
                    <span>
                      <strong>
                        {regels.indexOf(r) + 1}. {r.omschrijving}
                      </strong>
                      {r.subOmschrijving && <span className="fawkes-fct-item__sub">{r.subOmschrijving}</span>}
                    </span>
                  </div>
                ),
              },
              { key: "aantal", header: t("factuurDetailView.regels.col.aantal"), align: "center",
                cell: (r) => r.aantal },
              { key: "prijs", header: t("factuurDetailView.regels.col.prijs"), align: "right",
                cell: (r) => formatEuro(r.prijs, locale) },
              { key: "btw", header: t("factuurDetailView.regels.col.btw"), align: "right",
                cell: (r) => `${r.btwPercentage}%` },
              { key: "totaal", header: t("factuurDetailView.regels.col.totaal"), align: "right",
                cell: (r) => formatEuro(r.totaal, locale) },
              ...(canEdit ? [{
                key: "acties", header: t("factuurDetailView.regels.col.acties"), vast: true, align: "right" as const,
                cell: (r: typeof regels[number]) => (
                  <div className="fawkes-fct-row-actions">
                    <button
                      type="button"
                      aria-label={t("factuurDetailView.regels.wijzig")}
                      title={t("factuurDetailView.regels.wijzig")}
                      disabled={writing}
                      onClick={() => openBewerkRegel(r)}
                    >
                      <Pencil size={14} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="fawkes-fct-row-actions__danger"
                      aria-label={t("factuurDetailView.regels.verwijder")}
                      title={t("factuurDetailView.regels.verwijder")}
                      disabled={writing}
                      onClick={() => setConfirm({ kind: "regel", id: r.id, label: r.omschrijving })}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </div>
                ),
              }] : []),
            ]}
            rows={regels}
            getRowKey={(r) => String(r.id)}
            empty={t("factuurDetailView.regels.empty")}
          />
        </div>
        <div className="fawkes-fct-table-actions">
          <div className="fawkes-fct-table-actions__left">
            {/* Rendered for everyone but disabled without the role, so the action stays discoverable. */}
            <Button variant="primary" disabled={!canEdit || writing} onClick={() => setPickerOpen(true)}>
              <PlusCircleSolidIcon width={16} height={16} />
              {t("factuurDetailView.acties.regelToevoegen")}
            </Button>
          </div>
          <div className="fawkes-fct-totals">
            <div className="fawkes-fct-totals__row">
              <span>{t("factuurDetailView.samenvatting.subtotaal")}</span>
              <span>{formatEuro(sam.subtotaalExclBtw, locale)}</span>
            </div>
            {btwLive.map((b) => (
              <div className="fawkes-fct-totals__row" key={String(b.percentage)}>
                <span>
                  {t("factuurDetailView.samenvatting.btw")} {b.percentage}% {t("factuurDetailView.samenvatting.over")}{" "}
                  {formatEuro(b.grondslag, locale)}
                </span>
                <span>{formatEuro(b.bedrag, locale)}</span>
              </div>
            ))}
            {sam.kortingBedrag > 0 && (
              <div className="fawkes-fct-totals__row">
                <span>{t("factuurDetailView.samenvatting.korting")}</span>
                <span>- {formatEuro(sam.kortingBedrag, locale)}</span>
              </div>
            )}
            <div className="fawkes-fct-totals__row fawkes-fct-totals__row--total">
              <span>{t("factuurDetailView.samenvatting.totaalbedrag")}</span>
              <span>{formatEuro(sam.totaal, locale)}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="fawkes-fct-section">
        <div className="fawkes-fct-section__head">
          <h2>
            {t("factuurDetailView.tile.betalingen")} ({betalingen.length})
          </h2>
          <Button variant="secondary" disabled={!canEdit || writing} onClick={openPayment}>
            <Plus size={14} aria-hidden="true" />
            {t("factuurDetailView.acties.betaling")}
          </Button>
        </div>
        {volledigBetaald && (
          <div className="fawkes-factuur-banner fawkes-factuur-banner--paid">
            {t("factuurDetailView.betalingen.volledigBetaald")}
          </div>
        )}
        <div className="fawkes-fct-table-wrap">
          <IndeelbareTabel
            tabelKey="factuur-betalingen"
            columns={[
              { key: "datum", header: t("factuurDetailView.betalingen.col.datum"),
                cell: (b) => formatDateTime(b.datum, locale) },
              {
                key: "bedrag", header: t("factuurDetailView.betalingen.col.bedrag"), align: "right",
                cell: (b) => (
                  <span className={b.geannuleerd ? "fawkes-factuur-betaling--geannuleerd" : undefined}>
                    {formatEuro(b.bedrag, locale)}
                    {b.geannuleerd && ` (${t("factuurDetailView.betalingen.geannuleerd")})`}
                  </span>
                ),
              },
              {
                key: "methode", header: t("factuurDetailView.betalingen.col.methode"),
                cell: (b) => (b.methode ? (
                  <span className={`fawkes-factuur-paymethod fawkes-factuur-paymethod--${payMethodKey(b.methode)}`}>
                    {b.methode}
                  </span>
                ) : "—"),
              },
              { key: "referentie", header: t("factuurDetailView.betalingen.col.referentie"),
                cell: (b) => b.referentie ?? "—" },
              ...(canEdit ? [{
                key: "acties", header: t("factuurDetailView.regels.col.acties"), vast: true, align: "right" as const,
                cell: (b: typeof betalingen[number]) => (
                  <div className="fawkes-fct-row-actions">
                    {!b.geannuleerd && (
                      <button
                        type="button"
                        className="fawkes-fct-row-actions__danger"
                        aria-label={t("factuurDetailView.betalingen.annuleer")}
                        title={t("factuurDetailView.betalingen.annuleer")}
                        disabled={writing}
                        onClick={() => setConfirm({ kind: "betaling", id: b.id, label: formatEuro(b.bedrag, locale) })}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                ),
              }] : []),
            ]}
            rows={betalingen}
            getRowKey={(b) => String(b.id)}
            empty={t("factuurDetailView.betalingen.empty")}
          />
          {betalingen.length > 0 && (
            <p className="fawkes-fct-table-totaal">
              <span>{t("factuurDetailView.betalingen.totaalBetaald")}</span>
              <strong>{formatEuro(sam.betaald, locale)}</strong>
            </p>
          )}
        </div>
      </section>

      {/* Opmerkingen: the customer-facing memo, plus the dated internal notes log beneath it. */}
      <section className="fawkes-fct-section">
        <div className="fawkes-fct-section__head">
          <h2>{t("factuurDetailView.sectie.opmerkingen")}</h2>
          {canEdit && !noteEditing && (
            <button
              type="button"
              className="fawkes-fct__icon-btn"
              aria-label={t("factuurDetailView.bijlagen.bewerken")}
              title={t("factuurDetailView.bijlagen.bewerken")}
              onClick={() => {
                setNoteDraft(notitieTekst ?? "");
                setNoteEditing(true);
              }}
            >
              <Pencil size={16} aria-hidden="true" />
            </button>
          )}
        </div>
        {noteEditing ? (
          <div className="fawkes-factuur-note-edit">
            <TextareaField
              label={t("factuurDetailView.bijlagen.notitie")}
              rows={4}
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              aria-describedby="factuur-notitie-uitleg"
            />
            {/*
              Deze tekst komt op de factuur bij de klant, en het logboek eronder juist niet. Dat
              verschil stond alleen bij de interne notities ((niet zichtbaar voor klant)), dus wie
              de twee niet kent typte een interne aantekening in het verkeerde veld -- en dat is
              niet terug te draaien zodra de factuur de deur uit is.
            */}
            <p className="fawkes-factuur-notitie-uitleg" id="factuur-notitie-uitleg">
              {t("factuurDetailView.bijlagen.notitieUitleg")}
            </p>
            <div className="fawkes-factuur-acties">
              <Button onClick={saveNote} disabled={writing}>
                {t("common.save")}
              </Button>
              <Button variant="secondary" onClick={() => setNoteEditing(false)}>
                {t("common.cancel")}
              </Button>
            </div>
          </div>
        ) : notitieTekst ? (
          <>
            <h3 className="fawkes-factuur-notitie-kop">{t("factuurDetailView.bijlagen.opDeFactuur")}</h3>
            <p className="fawkes-factuur-notitie">{notitieTekst}</p>
          </>
        ) : (
          <p className="fawkes-tile-placeholder">{t("factuurDetailView.bijlagen.geenNotitie")}</p>
        )}

        <div className="fawkes-fct-intern">
          <div className="fawkes-fct-section__head">
            <h3>{t("factuurDetailView.sectie.interneNotities")}</h3>
            {canEdit && (
              <Button variant="secondary" disabled={writing} onClick={() => setLogAddOpen(true)}>
                <Plus size={14} aria-hidden="true" />
                {t("factuurDetailView.notitielog.toevoegen")}
              </Button>
            )}
          </div>
          {notities.length === 0 ? (
            <p className="fawkes-tile-placeholder">{t("factuurDetailView.notitielog.geen")}</p>
          ) : (
            <ol className="fawkes-factuur-notitielog">
              {notities.map((n) => (
                <li key={n.id} className="fawkes-factuur-notitielog__item">
                  <div className="fawkes-factuur-notitielog__head">
                    <span className="fawkes-factuur-notitielog__moment">{formatDateTime(n.datum, locale)}</span>
                    {n.melder && <span className="fawkes-factuur-notitielog__melder">{n.melder}</span>}
                    {canEdit && (
                      <Button
                        variant="secondary"
                        aria-label={t("factuurDetailView.notitielog.verwijder")}
                        title={t("factuurDetailView.notitielog.verwijder")}
                        disabled={writing}
                        onClick={() => setConfirm({ kind: "notitielog", id: n.id, label: n.notitie, bron: n.bron })}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                  <p className="fawkes-factuur-notitielog__tekst">{n.notitie}</p>
                  {n.opmerking && <p className="fawkes-factuur-notitielog__opmerking">{n.opmerking}</p>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>


      <Modal
        open={historieOpen}
        title={t("factuurDetailView.tile.tijdlijn")}
        onClose={() => setHistorieOpen(false)}
        footer={
          <Button variant="secondary" onClick={() => setHistorieOpen(false)}>
            {t("common.close")}
          </Button>
        }
      >
        <ol className="fawkes-factuur-tijdlijn">
          {d.tijdlijn.length === 0 && (
            <li className="fawkes-tile-placeholder">{t("factuurDetailView.tijdlijn.empty")}</li>
          )}
          {d.tijdlijn.map((e, i) => (
            <li
              key={`${e.type}-${e.moment}-${i}`}
              className={`fawkes-factuur-tijdlijn__item fawkes-factuur-tijdlijn__item--${e.type.toLowerCase()}`}
            >
              <span className="fawkes-factuur-tijdlijn__moment">
                {e.alleenDatum ? formatDate(e.moment, locale) : formatDateTime(e.moment, locale)}
              </span>
              <span className="fawkes-factuur-tijdlijn__titel">{e.titel}</span>
              {e.detail && <span className="fawkes-factuur-tijdlijn__detail">{e.detail}</span>}
              {e.door && (
                <span className="fawkes-factuur-tijdlijn__door">{t("factuurDetailView.tijdlijn.door", { args: [e.door] })}</span>
              )}
            </li>
          ))}
        </ol>
      </Modal>

      {/* Registering a payment is a counter action, so the dialog leads with what is owed and offers
          the method as one tap rather than a dropdown — the same method cards the wizards use. The
          amount opens on the outstanding balance because settling in full is the normal case. */}
      <Modal
        open={paymentOpen}
        title={t("factuurDetailView.acties.betaling")}
        onClose={() => setPaymentOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPaymentOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={submitPayment} disabled={!paymentValid || writing}>
              {t("common.save")}
            </Button>
          </>
        }
      >
        <div className="fawkes-fct-pay">
          <div className="fawkes-fct-pay__due">
            <span className="fawkes-fct-meta__label">{t("factuurDetailView.betaalstatus.openstaandLabel")}</span>
            <strong>{formatEuro(sam.openstaand, locale)}</strong>
          </div>

          <span className="fui-field__label">{t("factuurDetailView.betaling.wijze")}</span>
          <div className="fawkes-fct-pay__methods">
            {PAY_METHODS.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`fawkes-pay__method${payWijze === m.key ? " is-selected" : ""}`}
                aria-pressed={payWijze === m.key}
                onClick={() => setPayWijze(m.key)}
              >
                {payWijze === m.key && <Check className="fawkes-pay__method-check" size={14} aria-hidden="true" />}
                {m.icon}
                <span>{m.key}</span>
              </button>
            ))}
          </div>

          <div className="fawkes-fct-pay__amount">
            <Field
              label={t("factuurDetailView.betaling.bedrag")}
              type="number"
              min={0}
              step="0.01"
              value={payBedrag}
              onChange={(e) => setPayBedrag(e.target.value)}
            />
            <Button
              variant="secondary"
              className="fawkes-fct-pay__fill"
              disabled={sam.openstaand <= 0}
              onClick={() => setPayBedrag(String(sam.openstaand))}
            >
              {t("factuurDetailView.betaling.volledig")}
            </Button>
          </div>

          <Field
            label={t("factuurDetailView.betaling.datum")}
            type="date"
            value={payDatum}
            onChange={(e) => setPayDatum(e.target.value)}
          />

          {/* What the invoice still owes once this payment lands — the number the operator would
              otherwise work out on the spot, and the check that the amount was typed correctly. */}
          <div className="fawkes-fct-pay__rest">
            <span>{t("factuurDetailView.betaling.restant")}</span>
            <strong>{formatEuro(restantNaBetaling, locale)}</strong>
          </div>

          {/* The counter hands out no receipt, so the invoice goes to the customer by e-mail with this
              payment on it. Visible and tickable at the moment of saving — never a silent send. */}
          <CheckboxField
            label={
              hasKlantEmail
                ? t("factuurDetailView.betaling.mailen", { args: [d.klant?.email ?? ""] })
                : t("factuurDetailView.betaling.mailenGeenAdres")
            }
            checked={payEmail && hasKlantEmail}
            disabled={!hasKlantEmail || !canEdit}
            onChange={(e) => setPayEmail(e.target.checked)}
          />
        </div>
      </Modal>

      {/* Adding a line: the same catalog picker the intake and release wizards use. */}
      <ProductTariefPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={addLineFromPicker}
        t={t}
        locale={locale}
        feedPath="facturen/billable-items"
      />

      <Modal
        open={editLineOpen}
        title={t("factuurDetailView.regels.wijzig")}
        onClose={() => setEditLineOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditLineOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={submitLine} disabled={!lineValid || writing}>
              {t("common.save")}
            </Button>
          </>
        }
      >
        <Field label={t("factuurDetailView.regel.naam")} value={lineNaam} onChange={(e) => setLineNaam(e.target.value)} />
        <Field
          label={t("factuurDetailView.regel.aantal")}
          type="number"
          min={0}
          step="1"
          value={lineAantal}
          onChange={(e) => setLineAantal(e.target.value)}
        />
        <Field
          label={t("factuurDetailView.regel.prijs")}
          type="number"
          min={0}
          step="0.01"
          value={linePrijs}
          onChange={(e) => setLinePrijs(e.target.value)}
        />
        <SelectField
          label={t("factuurDetailView.regel.btw")}
          value={lineBtw}
          onChange={(e) => setLineBtw(e.target.value)}
          options={["0", "9", "21"].map((p) => ({ value: p, label: `${p}%` }))}
        />
      </Modal>

      <Modal
        open={logAddOpen}
        title={t("factuurDetailView.notitielog.toevoegen")}
        onClose={() => { setLogAddOpen(false); setLogFout(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setLogAddOpen(false); setLogFout(null); }}>
              {t("common.cancel")}
            </Button>
            <Button onClick={submitNoteLog} disabled={logNotitie.trim().length === 0 || writing}>
              {t("common.save")}
            </Button>
          </>
        }
      >
        {/* Alleen de notitie zelf. Het tweede veld "Opmerking (optioneel)" is eruit: niemand wist waar het
            voor was naast de notitie, en wat erin kwam was een tweede regel van hetzelfde (Maarten, 2026-10-23). */}
        <TextareaField
          label={t("factuurDetailView.notitielog.notitie")}
          rows={4}
          value={logNotitie}
          onChange={(e) => setLogNotitie(e.target.value)}
        />
        {logFout && <p className="fawkes-error" role="alert">{logFout}</p>}
      </Modal>

      <Modal
        open={confirm !== null}
        title={
          confirm?.kind === "betaling"
            ? t("factuurDetailView.betalingen.annuleer")
            : confirm?.kind === "herinnering"
              ? t("factuurDetailView.acties.herinnering")
              : confirm?.kind === "email"
                ? t("factuurDetailView.export.email")
                : confirm?.kind === "crediteren"
                  ? t("factuurDetailView.lifecycle.crediteren.titel")
                  : confirm?.kind === "dupliceren"
                    ? t("factuurDetailView.lifecycle.dupliceren.titel")
                    : confirm?.kind === "annuleren"
                      ? t("factuurDetailView.lifecycle.annuleren.titel")
                      : confirm?.kind === "notitielog"
                        ? t("factuurDetailView.notitielog.verwijder")
                        : t("factuurDetailView.regels.verwijder")
        }
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={runConfirm}
              // Mailen zonder adres heeft geen zin; de rest van de bevestigingen kent die eis niet.
              disabled={writing || (confirm?.kind === "email" && !hasKlantEmail && !mailNaar.includes("@"))}
            >
              {t("factuurDetailView.confirm.ja")}
            </Button>
          </>
        }
      >
        <p>
          {confirm?.kind === "email" && !hasKlantEmail
            ? t("factuurDetailView.export.emailAdresVragen")
            : confirm?.kind === "betaling"
            ? `${t("factuurDetailView.betalingen.annuleerBevestig")} (${confirm.label})`
            : confirm?.kind === "herinnering"
              ? t("factuurDetailView.confirm.herinnering")
              : confirm?.kind === "email"
                ? t("factuurDetailView.export.emailBevestig")
                : confirm?.kind === "crediteren"
                  ? t("factuurDetailView.lifecycle.crediteren.bevestig")
                  : confirm?.kind === "dupliceren"
                    ? t("factuurDetailView.lifecycle.dupliceren.bevestig")
                    : confirm?.kind === "annuleren"
                      ? t("factuurDetailView.lifecycle.annuleren.bevestig")
                      : confirm?.kind === "regel"
                        ? `${t("factuurDetailView.regels.verwijderBevestig")} (${confirm.label})`
                        : confirm?.kind === "notitielog"
                          ? t("factuurDetailView.notitielog.verwijderBevestig")
                          : null}
        </p>
        {/* Zonder klant is er geen adres om de factuur naartoe te sturen; dan vraagt het venster erom.
            Bevestigen blijft uit tot er iets staat dat op een adres lijkt. */}
        {confirm?.kind === "email" && !hasKlantEmail && (
          <Field
            label={t("factuurDetailView.export.emailAdres")}
            type="email"
            value={mailNaar}
            onChange={(e) => setMailNaar(e.target.value)}
          />
        )}
      </Modal>

      {/* Een factuur zonder debiteur moet in balans zijn: er is niemand om een restant aan te
          factureren, en een negatief saldo zou betekenen dat het asiel geld terug moet geven aan
          iemand die het niet kent (Maarten). Daarom kan hij niet bewaard worden zolang er iets
          openstaat -- de knop blijft wel bedienbaar en zegt wat eraan schort. */}
      {/* Opslaan en Annuleren horen onderaan en blijven in beeld: je werkt bovenin de factuur door en
          moet niet eerst naar beneden hoeven scrollen om te zien of er nog iets openstaat. Ze staan er
          altijd -- ook als er niets te bewaren valt -- zodat je weet waar je ze vindt. */}
      <div className="fawkes-fct__opslaanbalk">
        <span className="fawkes-fct__opslaanbalk-stand" role="status">
          {leegConcept
            ? t("factuurDetailView.concept.geenRegels")
            : losseVerkoopOnbalans
              ? t("factuurDetailView.concept.losseVerkoopOnbalans")
              : ietsTeBewaren
                ? t("factuurDetailView.concept.openstaand", { args: [String(wijzigingen.length)] })
                : t("factuurDetailView.concept.nietsOpenstaand")}
        </span>
        {/* Eigen labels, niet het kale "Opslaan": in de vensters op deze pagina staat dat woord ook,
            en dan weet je niet of je de regel bewaart of de hele factuur. */}
        <Button
          variant="secondary"
          disabled={(!ietsTeBewaren && !leegConcept) || opslaanBezig}
          onClick={leegConcept ? gooiLeegConceptWeg : annuleren}
        >
          {leegConcept ? t("factuurDetailView.concept.weggooienKnop") : t("factuurDetailView.concept.annulerenKnop")}
        </Button>
        {/* Opslaan blijft bedienbaar op een lege factuur, maar bewaart hem niet: hij zegt wat eraan
            ontbreekt. Een grijze knop vertelt je niet waarom hij grijs is. */}
        <Button
          variant="primary"
          disabled={(!ietsTeBewaren && !leegConcept) || opslaanBezig}
          onClick={leegConcept
            ? () => setNotice("geenRegels")
            : losseVerkoopOnbalans
              ? () => setNotice("losseVerkoopOnbalans")
              : opslaan}
        >
          {opslaanBezig ? t("factuurDetailView.concept.bezig") : t("factuurDetailView.concept.opslaanKnop")}
        </Button>
      </div>

      {/* Weglopen met werk dat nog nergens staat is het scenario dat deze hele conceptlaag moest
          voorkomen; daarom geen stil verlies maar een vraag met allebei de uitwegen erin. */}
      <Modal
        open={verlatenVraag}
        title={t("factuurDetailView.concept.verlatenTitel")}
        onClose={() => setVerlatenVraag(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setVerlatenVraag(false)}>
              {t("factuurDetailView.concept.blijven")}
            </Button>
            <Button variant="secondary" onClick={() => { setVerlatenVraag(false); annuleren(); onBack(); }}>
              {t("factuurDetailView.concept.weggooienEnWeg")}
            </Button>
            <Button
              variant="primary"
              disabled={opslaanBezig}
              onClick={async () => {
                await opslaan();
                setVerlatenVraag(false);
              }}
            >
              {t("factuurDetailView.concept.opslaanEnBlijven")}
            </Button>
          </>
        }
      >
        <p>{t("factuurDetailView.concept.verlatenUitleg", { args: [String(wijzigingen.length)] })}</p>
      </Modal>
    </div>
  );
}
